import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Avatar,
  ColumnDefinition,
  Empty,
  Pills,
} from "barte-design-system";
import { usePostHog } from "@posthog/react";

import { TableSkeleton } from "@shared/components";
import {
  formatDateToBr,
  formatTimeToBr,
  getInitialsFromEmail,
} from "@shared/helpers";
import { TRACK_EVENTS } from "@payfac/constants";
import { useGetRatePlansHistory } from "@payfac/services";
import { ERatePlanHistoryStatus } from "@payfac/types";

import {
  RATE_PLAN_HISTORY_PAGE_SIZE,
  RATE_PLAN_HISTORY_STATUS_LABEL_KEY,
  RATE_PLAN_HISTORY_STATUS_PILL_STATE,
  RATE_PLAN_HISTORY_STATUS_PILL_VARIANT,
} from "./rate-plan-history.constants";
import { ModalHistoryDetail } from "./components";

import styles from "./rate-plan-history.module.css";

import type { IRatePlanHistoryList } from "@payfac/types";
import type { RatePlanHistoryProps } from "./rate-plan-history.type";

const getHistoryStatus = (
  endedAt: string | null,
): keyof typeof ERatePlanHistoryStatus =>
  endedAt ? ERatePlanHistoryStatus.PREVIOUS : ERatePlanHistoryStatus.CURRENT;

export const RatePlanHistory = ({ planId }: RatePlanHistoryProps) => {
  const { t } = useTranslation();
  const posthog = usePostHog();
  const [page, setPage] = useState(1);
  const [selectedHistory, setSelectedHistory] =
    useState<IRatePlanHistoryList | null>(null);

  const { data: response, isLoading } = useGetRatePlansHistory({
    planId: Number(planId),
    filters: { page, limit: RATE_PLAN_HISTORY_PAGE_SIZE },
  });

  const history = response?.data?.data || [];

  const showEmptyState =
    !isLoading &&
    (history?.length === 0 || response?.data?.pagination?.total === 0);

  const onClickHistoryRow = (record: IRatePlanHistoryList) => {
    posthog.capture(TRACK_EVENTS.RATE_PLAN.HISTORY_DETAIL_CLICKED, {
      history_id: record.id,
      plan_id: planId,
    });

    setSelectedHistory(record);
  };

  const onCloseModal = () => setSelectedHistory(null);

  const columns: ColumnDefinition<IRatePlanHistoryList>[] = [
    {
      key: "startedAt",
      title: t("payfac.pageRatePlanHistoryTableColumnDate"),
      dataIndex: "startedAt",
      render: (startedAt) => (
        <div className={styles["date-cell"]}>
          <span>{formatDateToBr(startedAt as string)}</span>
          <span>{formatTimeToBr(startedAt as string)}</span>
        </div>
      ),
    },
    {
      key: "createdBy",
      title: t("payfac.pageRatePlanHistoryTableColumnAuthor"),
      dataIndex: "createdBy",
      render: (createdBy) => {
        const author = (createdBy as string) || "";
        const hasAuthor = Boolean(author);

        return (
          <div className={styles["author-cell"]}>
            <Avatar
              variant="text"
              size="sm"
              color="default"
              initials={
                hasAuthor
                  ? getInitialsFromEmail(author)
                  : t("payfac.pageRatePlanHistoryAuthorApiInitials")
              }
            />
            <span className={styles["author-email"]}>
              {hasAuthor
                ? author
                : t("payfac.pageRatePlanHistoryAuthorApi")}
            </span>
          </div>
        );
      },
    },
    {
      key: "status",
      title: t("payfac.pageRatePlanHistoryTableColumnStatus"),
      dataIndex: "endedAt",
      render: (endedAt) => {
        const statusKey = getHistoryStatus(endedAt as string | null);

        return (
          <Pills
            label={t(RATE_PLAN_HISTORY_STATUS_LABEL_KEY[statusKey])}
            state={RATE_PLAN_HISTORY_STATUS_PILL_STATE[statusKey]}
            variant={RATE_PLAN_HISTORY_STATUS_PILL_VARIANT[statusKey]}
            size="sm"
          />
        );
      },
    },
  ];

  useEffect(() => {
    if (planId) {
      posthog.capture(TRACK_EVENTS.RATE_PLAN.HISTORY_VIEWED, {
        plan_id: planId,
      });
    }
  }, [planId]);

  return (
    <div className={styles.container}>
      {showEmptyState && <Empty showButton={false} />}

      {!showEmptyState && (
        <div className={styles.table}>
          <TableSkeleton<IRatePlanHistoryList>
            isLoading={isLoading}
            columns={columns}
            data={history}
            rowKey={(record) => record.id}
            onRowClick={onClickHistoryRow}
            pagination={{
              currentPage: page,
              totalPages: response?.data?.pagination?.pages || 1,
              onPageChange: setPage,
            }}
          />
        </div>
      )}

      <ModalHistoryDetail
        isOpen={selectedHistory !== null}
        planId={planId}
        historyId={selectedHistory?.id ?? null}
        rowFallback={selectedHistory}
        onClose={onCloseModal}
      />
    </div>
  );
};
